/**
 * Calendar React Query Hooks
 * Custom hooks for managing calendar settings and filters using the new calendars API
 *
 * Note: This hook is used by GoldenWings Calendar to persist user settings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarsService } from '../api/services/calendars.service';

/**
 * Calendar settings type (persistent user preferences)
 */
export interface CalendarSettings {
  show_day_night?: boolean;
  show_weather?: boolean;
  default_view?: string;
  [key: string]: any;
}

/**
 * Calendar filters type (selected resources/users)
 */
export interface CalendarFilters {
  selected_resource_ids?: string[];
  selected_user_ids?: string[];
  [key: string]: any;
}

/**
 * Query key factory for calendar queries
 */
export const calendarKeys = {
  all: ['calendar'] as const,
  options: {
    all: () => [...calendarKeys.all, 'options'] as const,
    byKey: (key: string) => [...calendarKeys.all, 'options', key] as const,
    settings: () => [...calendarKeys.all, 'options', 'settings'] as const,
    filters: () => [...calendarKeys.all, 'options', 'filters'] as const,
  },
};

/**
 * Hook to fetch calendar settings
 * WORKAROUND: Backend doesn't support GET /calendars/options/{key} (405 Method Not Allowed)
 * Using localStorage as fallback
 *
 * @returns Query result with calendar settings
 */
export const useCalendarSettings = () => {
  return useQuery<CalendarSettings | null, Error>({
    queryKey: calendarKeys.options.settings(),
    queryFn: async () => {
      // Try localStorage first (workaround for missing GET endpoint)
      const stored = localStorage.getItem('goldenwings-calendar-settings');
      if (stored) {
        try {
          return JSON.parse(stored) as CalendarSettings;
        } catch (e) {
          console.warn('[useCalendar] Failed to parse stored settings:', e);
        }
      }

      // Try backend API (will likely fail with 405)
      try {
        const option = await calendarsService.getUserOption('settings');
        // If successful, store in localStorage for next time
        localStorage.setItem(
          'goldenwings-calendar-settings',
          JSON.stringify(option.value)
        );
        return option.value as CalendarSettings;
      } catch (error: any) {
        // If option doesn't exist (404) or method not allowed (405), return null
        if (error.response?.status === 404 || error.response?.status === 405) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch calendar filters
 * WORKAROUND: Backend doesn't support GET /calendars/options/{key} (405 Method Not Allowed)
 * Using localStorage as fallback
 *
 * @returns Query result with calendar filters
 */
export const useCalendarFilters = () => {
  return useQuery<CalendarFilters | null, Error>({
    queryKey: calendarKeys.options.filters(),
    queryFn: async () => {
      // Try localStorage first (workaround for missing GET endpoint)
      const stored = localStorage.getItem('goldenwings-calendar-filters');
      if (stored) {
        try {
          return JSON.parse(stored) as CalendarFilters;
        } catch (e) {
          console.warn('[useCalendar] Failed to parse stored filters:', e);
        }
      }

      // Try backend API (will likely fail with 405)
      try {
        const option = await calendarsService.getUserOption('filters');
        // If successful, store in localStorage for next time
        localStorage.setItem(
          'goldenwings-calendar-filters',
          JSON.stringify(option.value)
        );
        return option.value as CalendarFilters;
      } catch (error: any) {
        // If option doesn't exist (404) or method not allowed (405), return null
        if (error.response?.status === 404 || error.response?.status === 405) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to save calendar settings
 *
 * @returns Mutation for saving calendar settings
 */
export const useSaveCalendarSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: CalendarSettings) =>
      calendarsService.setUserOption({
        key: 'settings',
        value: settings,
        description: 'GoldenWings Calendar settings',
      }),
    onSuccess: (data, variables) => {
      // Store in localStorage (workaround for missing GET endpoint)
      localStorage.setItem(
        'goldenwings-calendar-settings',
        JSON.stringify(variables)
      );
      console.log(
        '[useCalendar] ✅ Settings saved to localStorage:',
        variables
      );

      // Invalidate settings query to trigger refetch from localStorage
      queryClient.invalidateQueries({
        queryKey: calendarKeys.options.settings(),
      });
    },
  });
};

/**
 * Hook to save calendar filters
 *
 * @returns Mutation for saving calendar filters
 */
export const useSaveCalendarFilters = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (filters: CalendarFilters) =>
      calendarsService.setUserOption({
        key: 'filters',
        value: filters,
        description: 'GoldenWings Calendar filters',
      }),
    onSuccess: (data, variables) => {
      // Store in localStorage (workaround for missing GET endpoint)
      localStorage.setItem(
        'goldenwings-calendar-filters',
        JSON.stringify(variables)
      );
      console.log('[useCalendar] ✅ Filters saved to localStorage:', variables);

      // Invalidate filters query to trigger refetch from localStorage
      queryClient.invalidateQueries({
        queryKey: calendarKeys.options.filters(),
      });
    },
  });
};
